import React from 'react';
import styled from 'styled-components';

import { AI_PROMPT_PUBLISHED_FILTER_OPTIONS } from 'constants/aiPrompts';
import type { AdminListPageConfig } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminAIPromptsQueryKeys, adminIndexAIPrompts, deleteAIPrompt } from 'services/api/admin/aiPrompts';
import { adminIndexClients } from 'services/api/admin/clients';
import type { AdminAIPromptIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';
import CopyIcon from 'svg/CopyIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import EyeIcon from 'svg/EyeIcon';
import PencilIcon from 'svg/PencilIcon';

import AssociatedClientsSlideout from '../workspacePrompts/AssociatedClientsSlideout';
import AssociatedGroupsSlideout from '../workspacePrompts/AssociatedGroupsSlideout';

import PromptPublishModal from './PromptPublishModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const PROMPT_INCLUDES = ['clientCount', 'groupCount', 'config', 'latestProcessPromptStatus'] as const;

type PromptRow = AIPrompt<AdminAIPromptIndexFields<(typeof PROMPT_INCLUDES)[number]>>;

type BuildPromptListConfigOpts = {
  targets: { name: string; id: string }[];
  title: string;
  columnStorageKey: string;
  addButtonLabel: string;
  archiveObjType: string;
  promptLabel: string;
  addPath: string;
  editPath: (id: string) => string;
  copyPath: (id: string) => string;
  defaultSort: string;
  clientFilterQueryKey: string;
  searchPlaceholder?: string;
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function buildPromptListConfig(opts: BuildPromptListConfigOpts): AdminListPageConfig<PromptRow> {
  return {
    title: opts.title,
    columnStorageKey: opts.columnStorageKey,
    queryKey: adminAIPromptsQueryKeys.index(),
    fetchFn: (params, signal) =>
      adminIndexAIPrompts(
        { ...params, targetClassname: params.targetClassname || opts.targets.map(({ id }) => id).join(',') },
        signal,
        [...PROMPT_INCLUDES]
      ),
    includes: PROMPT_INCLUDES,
    fetchParams: {
      defaultSort: opts.defaultSort,
    },
    searchPlaceholder: opts.searchPlaceholder,
    showResultCount: true,

    addButton: {
      label: opts.addButtonLabel,
      permission: 'adminAIPromptCreate',
      path: opts.addPath,
    },

    filters: [
      {
        key: 'client',
        label: 'Client',
        type: 'dropdown',
        fetchConfig: {
          queryKey: ['adminClients', opts.clientFilterQueryKey, 'filter'],
          fetchFn: adminIndexClients,
          params: { sortBy: 'name asc' },
        },
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      },
      ...(opts.targets.length > 1
        ? [
            {
              key: 'targetClassname',
              label: 'Target',
              type: 'dropdown' as const,
              staticOptions: opts.targets,
              getOptionLabel: (o: { name: string }) => o.name,
              getOptionValue: (o: { id: string }) => o.id,
            },
          ]
        : []),
      {
        key: 'isPublished',
        label: 'Published',
        type: 'dropdown',
        staticOptions: AI_PROMPT_PUBLISHED_FILTER_OPTIONS.slice(1),
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      },
    ],

    columns: [
      {
        id: 'name',
        header: 'Name',
        render: (row) => <BodySmallBold>{row.name || '—'}</BodySmallBold>,
      },
      {
        id: 'displayName',
        header: 'Display Name',
        render: (row) => row.displayName || '—',
      },
      {
        id: 'clientCount',
        header: 'Associated Clients',
        sortable: false,
        render: (row, ctx) => {
          if (!row.clientCount) return <BodySmallBold>&mdash;</BodySmallBold>;
          return <CountLink onClick={() => ctx.openSlideout('clients', row)}>{row.clientCount}</CountLink>;
        },
      },
      {
        id: 'groupCount',
        header: 'Associated Groups',
        sortable: false,
        render: (row, ctx) => {
          if (!row.groupCount) return <BodySmallBold>&mdash;</BodySmallBold>;
          return <CountLink onClick={() => ctx.openSlideout('groups', row)}>{row.groupCount}</CountLink>;
        },
      },
      {
        id: 'status',
        header: 'Published?',
        sortable: false,
        render: (row) => (
          <StatusCell>
            <StatusDot $on={row.isPublished} />
            {row.isPublished ? 'Yes' : 'No'}
          </StatusCell>
        ),
      },
      {
        id: 'updatedAt',
        header: 'Last Updated',
        render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '—'),
      },
    ],

    rowActions: [
      {
        label: 'Edit',
        icon: <PencilIcon width={18} height={18} />,
        permission: 'adminAIPromptEdit',
        onClick: (row, { navigate }) => navigate(opts.editPath(row.id)),
      },
      {
        label: 'View',
        icon: <EyeIcon width={18} height={18} />,
        visible: (row, ctx) => !ctx.profile.has(PERMISSIONS.adminAIPromptEdit),
        onClick: (row, { navigate }) => navigate(opts.editPath(row.id)),
      },
      {
        label: 'Publish',
        visible: (row, ctx) => ctx.profile.has(PERMISSIONS.adminAIPromptPublish) && !row.isPublished,
        disabled: (row) => !row.isProcessPromptComplete,
        onClick: (row, { openModal }) => openModal('publish', row),
      },
      {
        label: 'Copy',
        icon: <CopyIcon width={18} height={18} />,
        visible: (row, ctx) => ctx.profile.has(PERMISSIONS.adminAIPromptCreate) && !!row.isPublished,
        onClick: (row, { navigate }) => navigate(opts.copyPath(row.id)),
      },
      {
        label: 'Archive',
        icon: <DeactivateIcon width={18} height={18} />,
        danger: true,
        visible: (row, ctx) => !row.isPublished && !row.archived && ctx.profile.has(PERMISSIONS.adminAIPromptDelete),
        onClick: (row, { openModal }) => openModal('archive', row),
      },
    ],

    modals: {
      archive: {
        actionText: 'archive',
        objType: opts.archiveObjType,
        onConfirm: async (row) => {
          await deleteAIPrompt(row.id);
        },
      },
    },

    customModals: {
      publish: {
        render: (row, show, onClose, ctx) => (
          <PromptPublishModal
            isOpen={show}
            prompt={row}
            promptLabel={opts.promptLabel}
            onCancel={onClose}
            onComplete={() => {
              onClose();
              ctx.refetch();
            }}
          />
        ),
      },
    },

    slideouts: {
      clients: {
        render: (row, onClose) => (
          <AssociatedClientsSlideout promptId={row.id} promptName={row.displayName} handleClose={onClose} />
        ),
      },
      groups: {
        render: (row, onClose) => (
          <AssociatedGroupsSlideout promptId={row.id} promptName={row.displayName} handleClose={onClose} />
        ),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const StatusCell = styled.div`
  display: flex;
  align-items: center;
`;

const StatusDot = styled.div<{ $on: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $on }) => ($on ? colors.green100 : colors.black25)};
  margin-right: 12px;
`;

const CountLink = styled.span`
  color: ${colors.blue100};
  font-weight: 500;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
